import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";
import { z } from "zod";
import { useAuthStore } from "@/store/auth";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { forgotPassword, isLoading } = useAuthStore();
  const { settings: siteSettings } = useSiteSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError("");
      await forgotPassword(data.email);
      setIsSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to send reset email. Please try again."
      );
    }
  };

  if (isSuccess) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgImage="url('/assets/img/bg.jpg')"
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="cover"
        px={{ base: 4, sm: 6 }}
      >
        <Card
          w="full"
          maxW="448px"
          borderRadius="20px"
          boxShadow="0 10px 30px rgba(0,0,0,0.06)"
          bg="white"
        >
          <CardBody p={{ base: 6, sm: 8, md: 10 }}>
            <VStack spacing={{ base: 4, md: 6 }}>
              {/* Logo */}
              <Box textAlign="center" mb={{ base: 1, md: 2 }}>
                <img
                  src={siteSettings.auth_logo || "/assets/img/logo.jpg"}
                  alt="Logo"
                  style={{ maxWidth: "120px", margin: "0 auto", opacity: 0.9 }}
                />
              </Box>

              <Box textAlign="center">
                <CheckCircle
                  size={64}
                  color="#48BB78"
                  style={{ margin: "0 auto" }}
                />
                <Heading
                  size={{ base: "sm", md: "md" }}
                  color="green.500"
                  mb={3}
                  mt={4}
                  fontWeight="600"
                >
                  Check Your Email
                </Heading>
                <Text
                  color="gray.600"
                  fontSize={{ base: "xs", sm: "sm" }}
                  px={{ base: 2, sm: 0 }}
                >
                  We've sent a password reset link to your email address. Please
                  check your inbox and follow the instructions to reset your
                  password.
                </Text>
              </Box>

              <VStack spacing={4} w="full">
                <Button
                  w="full"
                  borderRadius="10px"
                  background="linear-gradient(90deg, #dc3611 0%, #fa886f 100%)"
                  color="white"
                  size={{ base: "md", md: "lg" }}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight="600"
                  _hover={{
                    background:
                      "linear-gradient(90deg, #c92f0e 0%, #f97960 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(220, 54, 17, 0.3)",
                  }}
                  _active={{
                    background:
                      "linear-gradient(90deg, #b32a0d 0%, #f86951 100%)",
                    transform: "translateY(0)",
                  }}
                  transition="all 0.2s"
                  onClick={() => navigate("/login")}
                >
                  Back to Login
                </Button>

                <Text
                  fontSize={{ base: "xs", sm: "sm" }}
                  color="gray.600"
                  textAlign="center"
                >
                  Didn't receive the email?{" "}
                  <Button
                    variant="link"
                    color="orange.500"
                    fontSize={{ base: "xs", sm: "sm" }}
                    fontWeight="600"
                    onClick={() => setIsSuccess(false)}
                    _hover={{
                      color: "orange.600",
                      textDecoration: "underline",
                    }}
                    px={0}
                  >
                    try again
                  </Button>
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
        <Text
          fontSize={{ base: "11px", sm: "14px" }}
          color="gray.900"
          position="fixed"
          bottom={{ base: "60px", md: "70px" }}
          fontWeight="400"
          textAlign="center"
          px={4}
        >
          Copyright ©2025 – ActionLabs
        </Text>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgImage="url('/assets/img/bg.jpg')"
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      px={{ base: 4, sm: 6 }}
    >
      <Card
        w="full"
        maxW="448px"
        borderRadius="20px"
        boxShadow="0 10px 30px rgba(0,0,0,0.06)"
        bg="white"
      >
        <CardBody p={{ base: 6, sm: 8, md: 10 }}>
          <VStack spacing={{ base: 4, md: 6 }}>
            {/* Logo */}
            <Box textAlign="center" mb={{ base: 1, md: 2 }}>
              <img
                src={siteSettings.auth_logo || "/assets/img/logo.jpg"}
                alt="Logo"
                style={{
                  maxWidth: "220px",
                  height: "66px",
                  width: "100%",
                  margin: "0 auto",
                  opacity: 0.9,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Heading */}
            <VStack spacing={2}>
              <Heading
                size={{ base: "sm", md: "md" }}
                fontWeight="700"
                color="#0A1B39"
              >
                Forgot Password?
              </Heading>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="#6C7688"
                textAlign="center"
                px={{ base: 2, sm: 0 }}
              >
                Enter your email address and we'll send you a link to reset your
                password.
              </Text>
            </VStack>

            {error && (
              <Alert
                status="error"
                borderRadius="md"
                fontSize={{ base: "xs", sm: "sm" }}
              >
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
              <VStack spacing={{ base: 4, md: 5 }}>
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel
                    fontSize={{ base: "xs", sm: "sm" }}
                    fontWeight="500"
                    color="gray.700"
                  >
                    Email Address
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.400"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <Mail size={18} />
                    </InputLeftElement>
                    <Input
                      {...register("email")}
                      type="email"
                      placeholder="Enter your email address"
                      borderColor="gray.300"
                      borderRadius="8px"
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{
                        borderColor: "orange.500",
                        boxShadow: "0 0 0 1px #ed8936",
                      }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      h={{ base: "40px", md: "48px" }}
                    />
                  </InputGroup>
                  <FormErrorMessage fontSize="xs">
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  borderRadius="10px"
                  background="linear-gradient(90deg, #dc3611 0%, #fa886f 100%)"
                  color="white"
                  size={{ base: "md", md: "lg" }}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight="600"
                  _hover={{
                    background:
                      "linear-gradient(90deg, #c92f0e 0%, #f97960 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(220, 54, 17, 0.3)",
                  }}
                  _active={{
                    background:
                      "linear-gradient(90deg, #b32a0d 0%, #f86951 100%)",
                    transform: "translateY(0)",
                  }}
                  isDisabled={isLoading}
                  transition="all 0.2s"
                >
                  {isLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Spinner size="sm" />
                      <Text>Sending reset link...</Text>
                    </Box>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </VStack>
            </Box>

            <Button
              variant="link"
              color="gray.600"
              fontSize={{ base: "xs", sm: "sm" }}
              fontWeight="500"
              onClick={() => navigate("/login")}
              leftIcon={<ArrowLeft size={16} />}
              _hover={{ color: "orange.500" }}
            >
              Back to Login
            </Button>
          </VStack>
        </CardBody>
      </Card>
      <Text
        fontSize={{ base: "11px", sm: "14px" }}
        color="gray.900"
        position="fixed"
        bottom={{ base: "60px", md: "70px" }}
        fontWeight="400"
        textAlign="center"
        px={4}
      >
        Copyright ©2025 – ActionLabs
      </Text>
    </Box>
  );
}
